import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { add, subHours } from 'date-fns';
import { ReadonlyURLSearchParams } from 'next/navigation';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const DIGIT_EXPRESSION = /^\d+$/;
export const isDigit = (character: string) => {
  return character && DIGIT_EXPRESSION.test(character);
};

// 브라우저가 목적지로 접속할 수 없는 호스트들(서버 바인딩 전용 주소)
const NON_ROUTABLE_HOSTS = new Set(['0.0.0.0', '::', '[::]']);

/**
 * redirect Location 으로 내려보낼 오리진을 안전하게 보정한다.
 * 0.0.0.0 / :: 같이 브라우저가 접속할 수 없는 호스트만 localhost 로 바꾸고,
 * 포트·프로토콜은 그대로 유지한다.
 * → dev(임의 포트), staging/prod(bareun.ai) 모두 그대로 동작한다.
 */
export function toBrowserBase(origin: string | URL): URL {
  const url = new URL(origin.toString());
  if (NON_ROUTABLE_HOSTS.has(url.hostname)) {
    url.hostname = 'localhost';
  }
  return url;
}

export const delay = (ms: number, callback?: () => void) => {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(ms);
      callback && callback();
    }, ms)
  );
};

export function convertEnumToArray(e: { [s: string]: string }) {
  return Object.keys(e).map((k) => ({
    id: k,
    text: e[k as keyof typeof e]
  }));
}

export interface PaginationResult {
  pagination: {
    offset: number;
    limit: number;
    column: string | undefined;
    order: 'asc' | 'desc' | undefined;
  };
  searchable: any;
  filterable: any;
  rangable: any;
}

export const getPaginationParams = ({
  searchParams,
  searchable = [],
  filterable = [],
  rangable = []
}: {
  searchParams: {
    [key: string]: string | string[] | undefined;
  };
  searchable?: ReadonlyArray<string>;
  filterable?: ReadonlyArray<string>;
  rangable?: ReadonlyArray<string>;
}): PaginationResult => {
  const { page, per_page, sort } = searchParams;
  // Number of items per page
  const limit = typeof per_page === 'string' ? parseInt(per_page) : 10;
  // Number of items to skip
  const offset =
    typeof page === 'string'
      ? parseInt(page) > 0
        ? (parseInt(page) - 1) * limit
        : 0
      : 0;
  // Column and order to sort by
  // Spliting the sort string by "." to get the column and order
  // Example: "title.desc" => ["title", "desc"]
  const [column, order] =
    typeof sort === 'string'
      ? (sort.split('.') as [string | undefined, 'asc' | 'desc' | undefined])
      : [];

  return {
    pagination: {
      offset,
      limit,
      column,
      order
    },
    searchable: searchable.reduce((p: any, f: string) => {
      const val = searchParams[f];
      p[f] = typeof val === 'string' ? (val.split(',') as string[]) : [];
      return p;
    }, {}),
    filterable: filterable.reduce((p: any, f: string) => {
      const val = searchParams[f];
      p[f] = typeof val === 'string' ? (val.split('.') as string[]) : [];
      return p;
    }, {}),
    rangable: rangable.reduce((p: any, f: string) => {
      const val = searchParams[f] as string;
      const fromTo = val?.split('|') ?? [];
      const isValid =
        fromTo.length === 2 &&
        isDateFormat(fromTo[0]) &&
        isDateFormat(fromTo[1]);
      p[f] = isValid
        ? {
            from: subHours(new Date(fromTo[0]), 9),
            to: subHours(
              add(new Date(fromTo[1]), {
                days: 1
              }),
              9
            )
          }
        : null;
      return p;
    }, {})
  };
};

export function setCookie(name: string, value: string, seconds: number) {
  var exdate = new Date();
  exdate.setSeconds(exdate.getSeconds() + seconds);
  var cookie_value =
    escape(value) +
    (seconds == 0 ? '' : ';  path=/; expires=' + exdate.toUTCString());
  document.cookie = name + '=' + cookie_value;
}

export const isDateFormat = (d: string | null) => {
  if (!d) {
    return false;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(d);
};

export const splitArray = (input: any[], spacing: number = 100) => {
  var output = [];

  for (var i = 0; i < input.length; i += spacing) {
    output.push(input.slice(i, i + spacing));
  }

  return output;
};

export const createQueryString = (
  searchParams: ReadonlyURLSearchParams,
  params: Record<string, string | number | null>
) => {
  const newSearchParams = new URLSearchParams(searchParams?.toString());

  for (const [key, value] of Object.entries(params)) {
    if (value === null) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, String(value));
    }
  }

  return newSearchParams.toString();
};

interface DownloadFileParams {
  url: string;
  fileName: string;
  downloadFileName: string;
  method?: string;
  params?: Record<string, string>;
}

export const DownloadFile = async ({
  url,
  fileName,
  downloadFileName,
  params = {},
  method = 'POST'
}: DownloadFileParams) => {
  let ftch = null;
  try {
    ftch = await fetch(url, {
      method,
      headers: {
        'Content-type': 'application/json'
      },
      body: JSON.stringify({ fileName, ...params })
    });

    if (ftch.status === 200) {
      const fileBlob = await ftch.blob();

      var link = document.createElement('a');
      const url = window.URL.createObjectURL(fileBlob);
      link.href = url;
      link.download = downloadFileName;
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  } catch (e) {
    console.log({ message: e, status: 400 });
  }
  return ftch;
};

export const HexToRGBA = (hex: string | undefined, alpha: number) => {
  if (hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return '';
};
export const entt = '&^$%#DFAX';

export function parseVersions(markdown: string) {
  // 릴리스 노트 항목을 분리
  const entries = markdown.split(/^#\s+/gm).filter(Boolean);

  const result = entries.map((entry, i) => {
    const lines = entry.trim().split('\n');
    const title = lines[0].trim(); // e.g. "3.0.rc3 (June 12, 2025)"

    const contentArray = lines.slice(1);
    contentArray.unshift(`# ${title}\n`);
    const content = contentArray.join('\n').trim();

    // 버전과 날짜 추출
    const match = title.match(/^([^\s]+)\s+\(([^)]+)\)/);
    const version = match ? match[1] : 'unknown';
    const date = match ? match[2] : 'unknown';

    return {
      version,
      date,
      content
    };
  });

  return result;
}

export const getToDay = () => new Date().toISOString().split('T')[0];

export const getCurrentMonth = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 0-indexed
  return `${year}-${month}`;
};

export function dateFormatted(date: Date, isPoint = false) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 두 자리로 변환
  const day = String(date.getDate()).padStart(2, '0'); // 두 자리로 변환

  if (isPoint) {
    return `${year}.${month}.${day}`;
  } else {
    return `${year}-${month}-${day}`;
  }
}

// 버전 문자열 비교 유틸 (3.0.rc3, 2.3.1 등 지원)
export function parseVer(v: string) {
  // 형식: 3.0.rc3 | 2.3.1 | 2.0.4 ...
  const m = v.match(/^(\d+)\.(\d+)(?:\.(\d+))?(?:\.rc(\d+))?$/);
  if (!m) return { major: 0, minor: 0, patch: 0, rc: null as number | null };
  const [, maj, min, pat, rc] = m;
  return {
    major: parseInt(maj, 10),
    minor: parseInt(min, 10),
    patch: pat ? parseInt(pat, 10) : 0,
    rc: rc ? parseInt(rc, 10) : null // rc가 있으면 프리릴리스
  };
}

// a > b 이면 1, 같으면 0, 작으면 -1
export function cmpVer(a: string, b: string) {
  const A = parseVer(a),
    B = parseVer(b);
  if (A.major !== B.major) return A.major > B.major ? 1 : -1;
  if (A.minor !== B.minor) return A.minor > B.minor ? 1 : -1;
  if (A.patch !== B.patch) return A.patch > B.patch ? 1 : -1;
  // 동일 버전일 때: 프리릴리스(rc)가 정식보다 낮게
  if (A.rc === null && B.rc === null) return 0;
  if (A.rc === null) return 1;
  if (B.rc === null) return -1;
  return A.rc > B.rc ? 1 : A.rc < B.rc ? -1 : 0;
}

export const isGt = (a: string, b: string) => cmpVer(a, b) === 1;
export const isGte = (a: string, b: string) => cmpVer(a, b) >= 0;

// utils/paymentPopup.ts
export function openCenteredPopup(
  url: string,
  name = 'paymentPopup',
  width = 480,
  height = 720
): Window | null {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const w = window.outerWidth ?? window.innerWidth;
  const h = window.outerHeight ?? window.innerHeight;

  const left = dualScreenLeft + (w - width) / 2;
  const top = dualScreenTop + (h - height) / 2;

  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${Math.max(0, left)}`,
    `top=${Math.max(0, top)}`,
    'resizable=yes',
    'scrollbars=yes',
    'status=yes'
  ].join(',');

  return window.open(url, name, features);
}

export function bigintToStringDeep<T>(data: T): any {
  if (typeof data === 'bigint') return data.toString();
  if (Array.isArray(data)) return data.map(bigintToStringDeep);
  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, bigintToStringDeep(v as any)])
    );
  }
  return data;
}
